import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { Plus, Pencil, Trash, Paperclip, ChevronDown, ChevronUp } from 'lucide-react';
import { SearchBar } from '../SearchBar/SearchBar';
import { Modal } from '../Modal/Modal';
import { PersonForm } from '../PersonForm/PersonForm';
import { api } from '../../services/api';
import recordOperationsDownload from '../../utils/recordOperationsDownload';

function operationalRoleText(person) {
  return String(person?.operationalRole || person?.operational_role || '').trim();
}

export function PeopleModule({ people, onAdd, onUpdate, onDelete, showHeader = true, showExtraHRFields = false, showCreateButton = true, onlyCollaboratorFields = false, showActivateButton = false, onActivated }) {
  const [query, setQuery] = useState('');
  const [emoFilter, setEmoFilter] = useState('all');
  const [positionFilter, setPositionFilter] = useState([]); // Cargo: selección múltiple, ej. ver todos los "electricistas" marcando sus puestos exactos
  const [positionFilterOpen, setPositionFilterOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showAttachments, setShowAttachments] = useState(false);
  const [attachmentsPerson, setAttachmentsPerson] = useState(null);
  const [attachmentsStatus, setAttachmentsStatus] = useState(null);
  const [serverList, setServerList] = useState([]);
  const [activateOpen, setActivateOpen] = useState(false);
  const [activateDoc, setActivateDoc] = useState('');
  const [activateLoading, setActivateLoading] = useState(false);
  const [activateResult, setActivateResult] = useState(null);
  const [activateOptions, setActivateOptions] = useState([]);
  const [activateMsg, setActivateMsg] = useState(null);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] = useState(false);
  const [contractEndDate, setContractEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  const formatActionError = (message) => String(message || '').replace(/^HTTP \d+:\s*/i, '');

  const personStatusLower = (p) => String(p?.status || p?.estado || '').trim().toLowerCase();

  // Cargos distintos ya presentes en los datos, para armar el filtro
  // multi-selección (ej. marcar "LINIERO CANASTA" + "OPERADOR DE REDES
  // ENERGIZADAS, EXPERTO" para ver de una vez a todos los "electricistas").
  const availablePositions = useMemo(() => {
    const set = new Set();
    (people || []).forEach((p) => {
      const pos = String(p?.position || '').trim();
      if (pos) set.add(pos);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [people]);

  const togglePositionFilter = (pos) => {
    setPositionFilter((prev) => (
      prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos]
    ));
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = people || [];
    if (onlyCollaboratorFields) {
      list = list.filter((p) => String(p?.status || '').trim().toLowerCase() === 'activo');
    }
    if (q) {
      list = list.filter((p) => [p.name, p.id, p.operationalRole, p.operational_role].some((v) => String(v ?? '').toLowerCase().includes(q)));
    }
    if (positionFilter.length > 0) {
      list = list.filter((p) => positionFilter.includes(String(p?.position || '').trim()));
    }
    if (emoFilter !== 'all') {
      list = list.filter((p) => {
        const d = daysTo(p.nextEmoDate);
        switch (emoFilter) {
          case 'upcoming':
            return d !== null && d >= 0 && d <= 30;
          case 'expired':
            return d !== null && d < 0;
          case 'ok':
            return d !== null && d > 30;
          case 'none':
            return !p.nextEmoDate;
          default:
            return true;
        }
      });
    }
    return list;
  }, [query, people, emoFilter, onlyCollaboratorFields, positionFilter]);

  const openNew = () => {
    setEditing(null);
    setIsOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setEditing(null);
  };

  const openAttachments = (p) => {
    setAttachmentsPerson(p);
    setShowAttachments(true);
  };

  const closeAttachments = () => {
    setShowAttachments(false);
    setAttachmentsPerson(null);
  };

  const exportToExcel = () => {
    const data = (people || []).map((p, i) => ({
        Contador: i + 1,
        Documento: p.id || '',
        Nombre: p.name || '',
        Cargo: String(p.position || '').trim() || '-',
        'Rol operativo': operationalRoleText(p) || 'Sin asignar',
        Proyecto: p.area || '',
        'Aplica extras': p.appliesExtras || '',
        'Años': computeAge(p.birthDate) || '',
        'Validación Cliente': p.clientValidation || '',
        Teléfono: p.phone || '',
        'Estado civil': p.civilStatus || '',
        Email: p.email || '',
        EPS: p.eps || '',
        'Fondo-Pensiones': p.pensionFund || '',
        AFP: p.afp || '',
        'Tipo de sangre': p.bloodType || '',
        'Fecha nacimiento': p.birthDate || '',
        'Fecha contrato': p.contractDate || '',
        'Número de contrato': p.contractNumber || '',
        'Modalidad contrato actual': p.currentContractModality || '',
        'Fecha otrosí': Array.isArray(p.otrosiDates) ? p.otrosiDates.filter(Boolean).join('; ') : (p.otrosiDate || ''),
        'Riesgo ARL %': p.arlRiskPercent || '',
        'EMO': p.emoDate || '',
        'Próximo EMO': p.nextEmoDate || '',
        Dirección: p.address || '',
        'Fecha de ingreso': p.hireDate || '',
        Formación: p.education || '',
        'Adjuntos (cantidad)': Array.isArray(p.attachments) ? p.attachments.length : (p.attachments ? 1 : 0),
        'Adjuntos (nombres)': Array.isArray(p.attachments) ? p.attachments.map(a => a?.name || '').filter(Boolean).join('; ') : '',
        'Firma (Nota)': p.signature ? 'Adjunta' : 'Sin firma',
        Estado: p.status || '',
      }));
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Personas');
    const now = new Date();
    const filename = `personas_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}.xlsx`;
    void recordOperationsDownload({ fileType: 'xlsx', moduleKey: 'almacen', reportName: 'Personas de almacén' });
    XLSX.writeFile(wb, filename);
  };

  const handleSave = async (data) => {
    const isEditExisting = editing && typeof editing.id !== 'undefined';
    if (isEditExisting) {
      const res = await onUpdate(editing.id, data);
      if (res && res.success === false) {
        window.alert(res.message || 'No se pudo actualizar la persona.');
        return;
      }
    } else {
      const res = await onAdd(data);
      if (res && res.success === false) {
        window.alert(res.message || 'No se pudo crear la persona.');
        return;
      }
    }
    closeModal();
  };

  const updateAttachments = async (personRef, nextAttachments) => {
    const base = personRef || attachmentsPerson;
    if (!base) return;
    const updated = { ...base, attachments: nextAttachments };
    const res = await onUpdate(base.id, updated);
    if (res && res.success === false) {
      window.alert(res.message || 'No se pudieron actualizar los adjuntos.');
      return;
    }
    setAttachmentsPerson(updated);
  };

  const handleAddAttachments = async (e) => {
    const files = Array.from(e.target?.files || []);
    if (!files.length || !attachmentsPerson) return;
    const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
    const isAllowed = (file) => {
      const type = (file.type || '').toLowerCase();
      const name = (file.name || '').toLowerCase();
      const ext = name.includes('.') ? name.split('.').pop() : '';
      const typeOk = type
        ? (type.includes('pdf') || type.includes('jpeg') || type.includes('png') || type.includes('jpg'))
        : (['pdf', 'jpeg', 'jpg', 'png'].includes(ext));
      const sizeOk = typeof file.size === 'number' ? file.size <= MAX_SIZE_BYTES : true;
      return { ok: typeOk && sizeOk };
    };
    const rejected = [];
    const valid = files.filter(f => {
      const { ok } = isAllowed(f);
      if (!ok) rejected.push(f);
      return ok;
    });
    const current = (() => {
      const raw = attachmentsPerson.attachments;
      if (Array.isArray(raw)) return raw.slice();
      if (raw) return [{ name: 'Adjunto', type: 'application/octet-stream', data: raw }];
      // Si el usuario ya validó "Ver en servidor", conservamos esos para no sobrescribir al agregar nuevos
      const rows = Array.isArray(serverList) ? serverList : [];
      const fromServer = rows
        .map((r) => {
          const name = String(r?.name || '').trim();
          const url = String(r?.url || '').trim();
          if (!name || !url) return null;
          return { name, url };
        })
        .filter(Boolean);
      return fromServer;
    })();
    try {
      if (!valid.length) {
        // No sobrescribir adjuntos existentes si el usuario canceló o seleccionó archivos inválidos
        try { e.target.value = ''; } catch (_) {}
        if (rejected.length) {
          setAttachmentsStatus({ type: 'error', text: `Se rechazaron ${rejected.length} archivo(s). Tipos permitidos: PDF, JPG, JPEG, PNG. Tamaño máximo por archivo: 10MB.` });
        }
        return;
      }
      const added = valid.map((file) => {
        let localUrl = '';
        try { localUrl = URL.createObjectURL(file); } catch (_) { localUrl = ''; }
        return { name: file.name, type: file.type, file, __localUrl: localUrl };
      });
      const next = [...current, ...added];
      await updateAttachments(attachmentsPerson, next);
      // reset input so same file can be selected again if needed
      e.target.value = '';
      if (rejected.length) {
        setAttachmentsStatus({ type: 'error', text: `Se rechazaron ${rejected.length} archivo(s). Tipos permitidos: PDF, JPG, JPEG, PNG. Tamaño máximo por archivo: 10MB.` });
      } else if (valid.length) {
        setAttachmentsStatus({ type: 'ok', text: `Se agregaron ${valid.length} archivo(s).` });
      }
    } catch (err) {
      console.error(err);
      window.alert('No se pudieron agregar los adjuntos.');
    }
  };

  const statusBadge = (status) => {
    const base = 'px-2 py-1 rounded-full text-xs font-medium';
    const cls = status === 'Activo' ? 'bg-green-100 text-green-700 border border-green-200' : 'bg-gray-100 text-gray-700 border border-gray-200';
    return <span className={`${base} ${cls}`}>{status}</span>;
  };

  const validationBadge = (val) => {
    const v = (val || '').toLowerCase();
    const base = 'px-2 py-1 rounded-full text-xs font-medium border';
    if (v === 'aprobado') return <span className={`${base} bg-green-100 text-green-700 border-green-200`}>Aprobado</span>;
    if (v === 'pendiente' || v === '') return <span className={`${base} bg-yellow-100 text-yellow-700 border-yellow-200`}>Pendiente</span>;
    if (v === 'no aplica') return <span className={`${base} bg-gray-100 text-gray-700 border-gray-200`}>No aplica</span>;
    return <span className={`${base} bg-gray-100 text-gray-700 border-gray-200`}>{val}</span>;
  };

  const daysTo = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const a = new Date(); a.setHours(0,0,0,0);
    const b = new Date(d); b.setHours(0,0,0,0);
    return Math.round((b - a) / 86400000);
  };

  const nextEmoStatus = (dateStr) => {
    const d = daysTo(dateStr);
    if (d === null) return { label: '-', cls: 'text-gray-400' };
    if (d < 0) return { label: `Vencido ${Math.abs(d)}d`, cls: 'bg-red-100 text-red-700 border border-red-200' };
    if (d <= 30) return { label: `Próximo ${d}d`, cls: 'bg-yellow-100 text-yellow-800 border border-yellow-200' };
    return { label: `En ${d}d`, cls: 'bg-green-100 text-green-700 border border-green-200' };
  };

  const parseBirthDate = (value) => {
    if (!value) return null;
    let d = new Date(value);
    if (!isNaN(d)) return d;
    const m1 = String(value).match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/);
    if (m1) {
      const day = parseInt(m1[1], 10);
      const month = parseInt(m1[2], 10) - 1;
      const year = parseInt(m1[3], 10);
      d = new Date(year, month, day);
      if (!isNaN(d)) return d;
    }
    const m2 = String(value).toLowerCase().match(/^(\d{1,2})\s+de\s+([a-zñ]+)(?:\s+de\s+(\d{2,4}))?$/i);
    if (m2) {
      const months = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];
      const day = parseInt(m2[1], 10);
      const monthIdx = months.indexOf(m2[2]);
      const year = m2[3] ? parseInt(m2[3], 10) : new Date().getFullYear();
      if (monthIdx >= 0) {
        d = new Date(year, monthIdx, day);
        if (!isNaN(d)) return d;
      }
    }
    return null;
  };

  const computeAge = (value) => {
    const d = parseBirthDate(value);
    if (!d) return '';
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age >= 0 ? age : '';
  };

  const parsePeopleGetResponse = (r) => {
    if (!r) return [];
    const d = r?.data;
    if (Array.isArray(d?.data)) return d.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object') return [d];
    return [];
  };

  const openActivate = () => {
    setActivateDoc('');
    setActivateResult(null);
    setActivateMsg(null);
    setDeactivateConfirmOpen(false);
    setContractEndDate(new Date().toISOString().split('T')[0]);
    setActivateOpen(true);
  };

  const closeActivate = () => {
    setActivateOpen(false);
    setActivateDoc('');
    setActivateResult(null);
    setActivateOptions([]);
    setActivateMsg(null);
    setActivateLoading(false);
    setDeactivateConfirmOpen(false);
  };

  const pickActivateOption = (p) => {
    setActivateOptions([]);
    setActivateResult(p);
    setDeactivateConfirmOpen(false);
    setActivateMsg(null);
  };

  // Busca por documento O por nombre (antes solo aceptaba cédula exacta,
  // lo que hacía casi imposible encontrar a alguien inactivo sin conocer
  // su número de documento de memoria).
  const searchToActivate = async () => {
    const term = String(activateDoc || '').trim();
    if (!term) {
      setActivateMsg({ type: 'error', text: 'Digita el documento o el nombre.' });
      setActivateResult(null);
      setActivateOptions([]);
      return;
    }
    setActivateLoading(true);
    setActivateMsg(null);
    setActivateResult(null);
    setActivateOptions([]);
    try {
      const isDni = /^\d+$/.test(term);
      const res = await api.people.get(isDni ? { dni: term, clean: 1, limit: 10 } : { q: term, clean: 1, limit: 10 });
      const list = parsePeopleGetResponse(res);
      if (list.length === 0) {
        setActivateMsg({ type: 'error', text: 'No se encontró ningún colaborador con esa búsqueda.' });
      } else if (list.length === 1) {
        pickActivateOption(list[0]);
      } else {
        setActivateOptions(list);
      }
    } catch (err) {
      setActivateMsg({ type: 'error', text: formatActionError(err?.message) || 'No se pudo consultar.' });
      setActivateResult(null);
    } finally {
      setActivateLoading(false);
    }
  };

  const doActivate = async () => {
    const p = activateResult;
    const dni = String(p?.dni || p?.id || '').trim();
    const dbId = Number(p?._dbId || 0) || 0;
    if (!dni && !dbId) {
      setActivateMsg({ type: 'error', text: 'No hay colaborador seleccionado.' });
      return;
    }
    setActivateLoading(true);
    setActivateMsg(null);
    try {
      const res = await api.people.activate(dbId ? { id: dbId } : { dni });
      if (!res?.ok) {
        throw new Error(res?.error || 'No se pudo activar.');
      }
      const updated = res?.data || p;
      const name = String(updated?.name || p?.name || 'Colaborador').trim();
      setActivateMsg({ type: 'ok', text: `Colaborador ${name} activado correctamente.` });
      setActivateResult(updated);
      try { if (typeof onActivated === 'function') onActivated(updated); } catch (_) {}
    } catch (err) {
      setActivateMsg({ type: 'error', text: formatActionError(err?.message) || 'No se pudo activar.' });
    } finally {
      setActivateLoading(false);
    }
  };

  const doDeactivate = async () => {
    const p = activateResult;
    const id = String(p?.id || p?.dni || '').trim();
    if (!id) {
      setActivateMsg({ type: 'error', text: 'No hay colaborador seleccionado.' });
      return;
    }
    const endDate = String(contractEndDate || '').trim();
    if (!endDate) {
      setActivateMsg({ type: 'error', text: 'Indica la fecha de retiro.' });
      return;
    }
    setActivateLoading(true);
    setActivateMsg(null);
    try {
      const res = await onDelete(id, { contractEndDate: endDate });
      if (res && res.success === false) {
        setActivateMsg({ type: 'error', text: formatActionError(res.message) || 'No se pudo desactivar.' });
        setDeactivateConfirmOpen(false);
        return;
      }
      const name = String(p?.name || 'Colaborador').trim();
      setActivateMsg({ type: 'ok', text: `Colaborador ${name} desactivado correctamente.` });
      setActivateResult({ ...p, status: 'Inactivo', estado: 'Inactivo', contractEndDate: endDate });
      setDeactivateConfirmOpen(false);
      try { if (typeof onActivated === 'function') onActivated(); } catch (_) {}
    } catch (err) {
      setActivateMsg({ type: 'error', text: formatActionError(err?.message) || 'No se pudo desactivar.' });
      setDeactivateConfirmOpen(false);
    } finally {
      setActivateLoading(false);
    }
  };

  return (
    <div>
      <div className={`flex items-center mb-4 ${showHeader ? 'justify-between' : 'justify-end'}`}>
        {showHeader && (
          <h3 className="text-xl font-semibold">Gestión de Personas</h3>
        )}
        <div className="flex items-center gap-2">
          {showCreateButton && (
            <button
              type="button"
              onClick={openNew}
              className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-md text-sm"
            >
              <Plus size={18} /> Nueva Persona
            </button>
          )}
          {showActivateButton && (
            <button
              type="button"
              onClick={openActivate}
              className="flex items-center gap-2 bg-gray-700 hover:bg-gray-800 text-white px-3 py-2 rounded-md text-sm"
            >
              Activar / desactivar colaborador
            </button>
          )}
          <button
            type="button"
            onClick={exportToExcel}
            className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-md text-sm"
          >
            Exportar
          </button>
        </div>
      </div>

      <SearchBar
        value={query}
        onChange={setQuery}
        placeholder="Buscar por nombre, documento o rol operativo..."
      />

      {/* Filtro por Cargo: selección múltiple para ver de una vez a
          todo un grupo (ej. marcar todos los cargos eléctricos, ambientales,
          SST...) sin depender de que el cargo diga literalmente esa palabra. */}
      <div className="mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setPositionFilterOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 border rounded px-3 py-1.5 text-sm bg-white hover:bg-gray-50 text-gray-700"
          >
            Cargo{positionFilter.length > 0 ? ` (${positionFilter.length})` : ''}
            {positionFilterOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {positionFilter.length > 0 && (
            <button
              type="button"
              onClick={() => setPositionFilter([])}
              className="text-xs text-blue-600 hover:underline"
            >
              Limpiar filtro
            </button>
          )}
        </div>
        {positionFilterOpen && (
          <div className="mt-2 border rounded-lg bg-white p-3 max-h-64 overflow-y-auto">
            {availablePositions.length === 0 ? (
              <span className="text-xs text-gray-500">No hay cargos registrados</span>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5">
                {availablePositions.map((pos) => (
                  <label key={pos} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={positionFilter.includes(pos)}
                      onChange={() => togglePositionFilter(pos)}
                    />
                    <span className="truncate" title={pos}>{pos}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Filtro por Próximo EMO */}
      {!onlyCollaboratorFields && (
        <div className="flex items-center gap-2 mb-3">
          <label className="text-sm text-gray-700">Próximo EMO:</label>
          <select
            value={emoFilter}
            onChange={(e) => setEmoFilter(e.target.value)}
            className="border rounded px-2 py-1 text-sm bg-white"
          >
            <option value="all">Todos</option>
            <option value="upcoming">Próximo (≤ 30 días)</option>
            <option value="expired">Vencido</option>
            <option value="ok">En más de 30 días</option>
            <option value="none">Sin fecha</option>
          </select>
        </div>
      )}

      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full text-sm">
          <thead>
            {onlyCollaboratorFields ? (
              <tr className="bg-gray-100 text-gray-700">
                <th className="px-4 py-2 text-left">Documento</th>
                <th className="px-4 py-2 text-left">Nombre</th>
                <th className="px-4 py-2 text-left whitespace-nowrap">Cargo</th>
                <th className="px-4 py-2 text-left whitespace-nowrap">Rol operativo</th>
                <th className="px-4 py-2 text-left">Proyecto</th>
                <th className="px-4 py-2 text-left">Validación Cliente</th>
                <th className="px-4 py-2 text-left">Teléfono</th>
                <th className="px-4 py-2 text-left">Modalidad contrato</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-left">Acciones</th>
              </tr>
            ) : (
              <tr className="bg-gray-100 text-gray-700">
                <th className="px-4 py-2 text-left">Documento</th>
                <th className="px-4 py-2 text-left">Nombre</th>
                <th className="px-4 py-2 text-left whitespace-nowrap">Cargo</th>
                <th className="px-4 py-2 text-left whitespace-nowrap">Rol operativo</th>
                <th className="px-4 py-2 text-left">Proyecto</th>
                <th className="px-4 py-2 text-left">Validación Cliente</th>
                <th className="px-4 py-2 text-left">Teléfono</th>
                <th className="px-4 py-2 text-left">EPS</th>
                <th className="px-4 py-2 text-left">Fondo-Pensiones</th>
                <th className="px-4 py-2 text-left">AFP</th>
                <th className="px-4 py-2 text-left">Tipo de sangre</th>
                <th className="px-4 py-2 text-left">Fecha nacimiento</th>
                <th className="px-4 py-2 text-left">Años</th>
                <th className="px-4 py-2 text-left">Modalidad contrato</th>
                <th className="px-4 py-2 text-left">EMO</th>
                <th className="px-4 py-2 text-left">Próximo EMO</th>
                <th className="px-4 py-2 text-left">Firma</th>
                <th className="px-4 py-2 text-left">Estado</th>
                <th className="px-4 py-2 text-left">Acciones</th>
              </tr>
            )}
          </thead>
          <tbody>
            {filtered.map((p, index) => (
              <tr key={p.id} className="border-t">
                <td className="px-4 py-2">{p.id}</td>
                <td className="px-4 py-2">{p.name}</td>
                <td className="px-4 py-2 whitespace-nowrap">{String(p.position || '').trim() || '-'}</td>
                <td className="px-4 py-2 whitespace-nowrap">{operationalRoleText(p) || <span className="text-gray-400">Sin asignar</span>}</td>
                <td className="px-4 py-2">{p.area}</td>
                {onlyCollaboratorFields ? (
                  <>
                    <td className="px-4 py-2">{validationBadge(p.clientValidation)}</td>
                    <td className="px-4 py-2">{p.phone}</td>
                    <td className="px-4 py-2">{p.currentContractModality || p.contractType || ''}</td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2">{validationBadge(p.clientValidation)}</td>
                    <td className="px-4 py-2">{p.phone}</td>
                    <td className="px-4 py-2">{p.eps}</td>
                    <td className="px-4 py-2">{p.pensionFund}</td>
                    <td className="px-4 py-2">{p.afp}</td>
                    <td className="px-4 py-2">{p.bloodType}</td>
                    <td className="px-4 py-2">{p.birthDate}</td>
                    <td className="px-4 py-2">{computeAge(p.birthDate) || '-'}</td>
                    <td className="px-4 py-2">{p.currentContractModality}</td>
                    <td className="px-4 py-2">{p.emoDate}</td>
                    <td className="px-4 py-2">
                      {p.nextEmoDate ? (
                        <div className="flex items-center gap-2">
                          <span>{p.nextEmoDate}</span>
                          {(() => { const s = nextEmoStatus(p.nextEmoDate); return <span className={`px-2 py-0.5 rounded-full text-xs ${s.cls}`}>{s.label}</span>; })()}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {p.signature ? (
                        <img src={p.signature} alt="Firma digital" className="h-10 max-w-[120px] object-contain border rounded" />
                      ) : (
                        <span className="text-xs text-gray-500">Sin firma</span>
                      )}
                    </td>
                  </>
                )}
                <td className="px-4 py-2">{statusBadge(p.status)}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="text-gray-700 hover:text-gray-900"
                      onClick={() => openAttachments(p)}
                      title="Ver adjuntos"
                      aria-label="Ver adjuntos"
                    >
                      <Paperclip size={18} />
                    </button>
                    <button
                      type="button"
                      className="text-blue-600 hover:text-blue-700"
                      onClick={() => openEdit(p)}
                      aria-label="Editar"
                    >
                      <Pencil size={18} />
                    </button>
                    {!showActivateButton && (
                      <button
                        type="button"
                        className="text-red-600 hover:text-red-700"
                        onClick={async () => {
                          const res = await onDelete(p.id);
                          if (res && res.success === false) {
                            window.alert(res.message || 'No se pudo eliminar la persona.');
                          }
                        }}
                        aria-label="Eliminar"
                      >
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={onlyCollaboratorFields ? 10 : 20} className="px-4 py-6 text-center text-gray-500">No se encontraron personas.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={isOpen} onClose={closeModal} title={editing ? 'Editar Persona' : 'Nueva Persona'} size="max-w-4xl">
        <PersonForm person={editing} onSave={handleSave} onCancel={closeModal} showExtraHRFields={showExtraHRFields} activePeople={people} />
      </Modal>

      <Modal isOpen={showAttachments} onClose={closeAttachments} title={`Adjuntos ${attachmentsPerson?.name ? 'de ' + attachmentsPerson.name : ''}`}>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Agregar adjuntos:</label>
            <input
              type="file"
              multiple
              accept="image/*,application/pdf"
              onChange={handleAddAttachments}
              className="text-sm"
            />
            <div className="text-xs text-gray-500">Los archivos se guardan automáticamente.</div>
          </div>
          <div>
            <button
              type="button"
              className="text-xs px-2 py-1 border rounded bg-white hover:bg-gray-50"
              onClick={async () => {
                try {
                  const dni = String(attachmentsPerson?.dni || attachmentsPerson?.id || '').trim();
                  const r = await api.hr.files.list({ category: 'adjuntos', dni });
                  const rows = Array.isArray(r?.data) ? r.data : [];
                  setServerList(rows);
                } catch (_) { setServerList([]); }
              }}
            >Ver en servidor</button>
          </div>
          {Array.isArray(serverList) && serverList.length > 0 && (
            <div className="border rounded p-2 bg-gray-50">
              <div className="text-xs mb-1">Servidor ({serverList.length})</div>
              <ul className="text-xs space-y-1">
                {serverList.map((f, i) => (
                  <li key={i} className="flex items-center justify-between gap-2">
                    <a href={f.url || '#'} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{f.name}</a>
                    <span className="text-gray-500">{f.url ? '' : 'sin url'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {attachmentsStatus && (
            <div className={`text-xs ${attachmentsStatus.type === 'error' ? 'text-red-600' : 'text-gray-700'}`}>{attachmentsStatus.text}</div>
          )}
          <div className="max-h-[60vh] overflow-auto">
            {(() => {
              const raw = attachmentsPerson?.attachments;
              const items = Array.isArray(raw)
                ? raw
                : (raw ? [{ name: 'Adjunto', type: 'application/octet-stream', data: raw }] : []);
              return (
                <>
                  {items.length === 0 && (
                    <div className="text-sm text-gray-600">Sin adjuntos.</div>
                  )}
                  {items.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {items.map((a, i) => {
                        const name = (a && typeof a === 'object' ? a.name : '') || `Adjunto ${i + 1}`;
                        const typeRaw = (a && typeof a === 'object' ? a.type : '') || '';
                        const ext = String(name).toLowerCase().includes('.') ? String(name).toLowerCase().split('.').pop() : '';
                        const isImg = typeRaw.startsWith('image/') || ['jpg','jpeg','png','webp'].includes(ext);
                        const isPdf = typeRaw.includes('pdf') || ext === 'pdf';
                        const href =
                          (a && typeof a === 'object' && typeof a.data === 'string' && a.data) ? a.data
                          : (a && typeof a === 'object' && typeof a.__localUrl === 'string' && a.__localUrl) ? a.__localUrl
                          : (a && typeof a === 'object' && typeof a.url === 'string' && a.url) ? a.url
                          : (typeof a === 'string' ? a : '');
                        return (
                          <div key={i} className="border rounded p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <div className="text-xs text-gray-700 break-words">{name}</div>
                              <button
                                type="button"
                                className="text-red-600 hover:text-red-700"
                                title="Eliminar adjunto"
                                aria-label="Eliminar adjunto"
                                onClick={() => {
                                  if (a && typeof a === 'object' && typeof a.__localUrl === 'string' && a.__localUrl) {
                                    try { URL.revokeObjectURL(a.__localUrl); } catch (_) {}
                                  }
                                  const next = items.filter((_, idx) => idx !== i);
                                  updateAttachments(attachmentsPerson, next);
                                }}
                              >
                                <Trash size={16} />
                              </button>
                            </div>
                            {isImg && href && (
                              <img src={href} alt={name} className="w-full h-48 object-contain bg-white border" />
                            )}
                            {!isImg && isPdf && href && (
                              <iframe title={name} src={href} className="w-full h-48 bg-white border" />
                            )}
                            {!isImg && !isPdf && (
                              <div className="text-xs text-gray-600">Tipo: {typeRaw || 'desconocido'}</div>
                            )}
                            {href && (
                              <a
                                href={href}
                                download={name}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm"
                              >
                                Descargar / Abrir
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </Modal>

      <Modal isOpen={activateOpen} onClose={closeActivate} title="Activar / desactivar colaborador">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
            <div>
              <label className="block text-sm font-medium mb-1">Documento o nombre</label>
              <input
                value={activateDoc}
                onChange={(e) => setActivateDoc(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm"
                placeholder="Cédula o nombre completo"
              />
            </div>
            <button
              type="button"
              onClick={searchToActivate}
              disabled={activateLoading}
              className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50"
            >
              {activateLoading ? 'Buscando...' : 'Buscar'}
            </button>
          </div>

          {activateMsg && (
            <div
              role={activateMsg.type === 'error' ? 'alert' : 'status'}
              className={`text-sm rounded-md border px-3 py-2 ${
                activateMsg.type === 'error'
                  ? 'bg-red-50 text-red-800 border-red-200'
                  : 'bg-green-50 text-green-800 border-green-200'
              }`}
            >
              {activateMsg.text}
            </div>
          )}

          {activateOptions.length > 0 && (
            <div className="border rounded-lg divide-y max-h-64 overflow-y-auto">
              {activateOptions.map((p) => (
                <button
                  key={`${p.dni || p.id}-${p._dbId || ''}`}
                  type="button"
                  onClick={() => pickActivateOption(p)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm"
                >
                  <div className="font-medium text-gray-900">{p.name || 'Sin nombre'}</div>
                  <div className="text-xs text-gray-500">
                    Doc: {p.dni || p.id || '—'} · Estado: {String(p.status || p.estado || '—')}
                  </div>
                </button>
              ))}
            </div>
          )}

          {activateResult && (() => {
            const st = personStatusLower(activateResult);
            const isActive = st === 'activo';
            const isInactive = st === 'inactivo';
            const displayName = activateResult?.name || 'Sin nombre';
            return (
            <div className="border rounded-lg p-3 bg-gray-50 space-y-2">
              <div className="text-sm font-medium text-gray-900">{displayName}</div>
              <div className="text-xs text-gray-700">
                Documento: <span className="font-mono">{activateResult?.dni || activateResult?.id || '—'}</span>
              </div>
              <div className="text-xs text-gray-700">
                Estado: {String(activateResult?.status || activateResult?.estado || '—')}
              </div>
              {isActive && !deactivateConfirmOpen && (
                <div>
                  <label className="block text-sm font-medium mb-1">Fecha de retiro</label>
                  <input
                    type="date"
                    value={contractEndDate}
                    onChange={(e) => setContractEndDate(e.target.value)}
                    className="w-full border rounded px-3 py-2 text-sm bg-white"
                    disabled={activateLoading}
                  />
                </div>
              )}
              {isActive && deactivateConfirmOpen && (
                <p className="text-sm text-gray-800">
                  ¿Confirmas desactivar a <span className="font-medium">{displayName}</span>?
                </p>
              )}
              <div className="flex items-center justify-end gap-2 pt-2 flex-wrap">
                <button
                  type="button"
                  onClick={closeActivate}
                  className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm"
                  disabled={activateLoading}
                >
                  Cerrar
                </button>
                {isInactive && (
                  <button
                    type="button"
                    onClick={doActivate}
                    className="px-3 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm disabled:opacity-50"
                    disabled={activateLoading}
                  >
                    {activateLoading ? 'Activando...' : 'Activar'}
                  </button>
                )}
                {isActive && !deactivateConfirmOpen && (
                  <button
                    type="button"
                    onClick={() => {
                      setActivateMsg(null);
                      setDeactivateConfirmOpen(true);
                    }}
                    className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50"
                    disabled={activateLoading}
                  >
                    Desactivar
                  </button>
                )}
                {isActive && deactivateConfirmOpen && (
                  <>
                    <button
                      type="button"
                      onClick={() => setDeactivateConfirmOpen(false)}
                      className="px-3 py-2 rounded border bg-white hover:bg-gray-50 text-sm"
                      disabled={activateLoading}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={doDeactivate}
                      className="px-3 py-2 rounded bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50"
                      disabled={activateLoading}
                    >
                      {activateLoading ? 'Desactivando...' : 'Confirmar desactivación'}
                    </button>
                  </>
                )}
              </div>
            </div>
            );
          })()}
        </div>
      </Modal>
    </div>
  );
}
